// ============================================================
// 📋 SuiviDemandeModal.jsx
// Modal de tracking visuel d'une demande de congé
// Affiche la timeline de validation : Création → N+1 → N+2 → Final
// ============================================================

import React, { useEffect, useState, useCallback } from 'react';
import api from '../../services/api';
import { X, Check, Clock, Ban, User } from 'lucide-react';

export default function SuiviDemandeModal({ isOpen, onClose, demande }) {
  
  // ==========================================================
  // 📊 ÉTATS
  // ==========================================================
  const [suivi, setSuivi] = useState(null);       // Infos de la demande
  const [etapes, setEtapes] = useState([]);       // Étapes de validation
  const [loading, setLoading] = useState(false);  // Chargement

  // ==========================================================
  // 🔄 CHARGEMENT DU SUIVI
  // ==========================================================

  // ✅ Correction : useCallback pour fetchSuivi
  const fetchSuivi = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/demandes/suivi/${demande.ID_DdeConges}`);
      if (res.data.success) {
        setSuivi(res.data.demande);
        setEtapes(res.data.etapes || []);
      }
    } catch (err) {
      console.error('Erreur chargement suivi:', err);
    } finally {
      setLoading(false);
    }
  }, [demande?.ID_DdeConges]); // ✅ Dépendance

  // ✅ Correction : ajout de fetchSuivi dans les dépendances
  useEffect(() => {
    if (isOpen && demande?.ID_DdeConges) {
      fetchSuivi();
    }
  }, [isOpen, demande, fetchSuivi]); // ✅ Dépendance corrigée

  // ==========================================================
  // 🎨 HELPERS D'AFFICHAGE
  // ==========================================================
  
  // Icône selon la décision
  const getStatusIcon = (decision) => {
    if (decision === 'Approuve') return <Check size={18} className="text-green-500" />;
    if (decision === 'Rejete') return <Ban size={18} className="text-red-500" />;
    return <Clock size={18} className="text-amber-500" />;
  };

  // Couleur de fond selon la décision
  const getStatusBg = (decision) => {
    if (decision === 'Approuve') return 'bg-green-100 border-green-300';
    if (decision === 'Rejete') return 'bg-red-100 border-red-300';
    return 'bg-amber-100 border-amber-300';
  };

  // ==========================================================
  // 🚫 MODALE FERMÉE
  // ==========================================================
  if (!isOpen || !demande) return null;

  // ==========================================================
  // 🎨 RENDU
  // ==========================================================
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50">
      
      {/* Conteneur modale avec scrollbar esthétique */}
      <div className="bg-white rounded-t-2xl sm:rounded-xl w-full sm:max-w-lg max-h-[90vh] overflow-y-auto animate-slide-up">
        
        {/* ================================================== */}
        {/* HEADER STICKY */}
        {/* ================================================== */}
        <div className="sticky top-0 bg-white rounded-t-2xl border-b px-5 py-4 flex justify-between items-center z-10">
          <h3 className="text-lg font-semibold text-emerald-600">📋 Suivi de la demande</h3>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600">
            <X size={22} />
          </button>
        </div>

        {/* ================================================== */}
        {/* CHARGEMENT */}
        {/* ================================================== */}
        {loading ? (
          <div className="p-8 text-center text-gray-400 text-sm">Chargement...</div>
        ) : (
          <div className="p-5">
            
            {/* ============================================== */}
            {/* 📋 RÉSUMÉ DE LA DEMANDE */}
            {/* ============================================== */}
            <div className="bg-emerald-50 rounded-xl p-4 mb-5">
              <p className="text-sm font-bold text-emerald-800">
                {demande.TypeCongeLibelle || suivi?.TypeCongeLibelle}
              </p>
              <p className="text-xs text-emerald-600 mt-1">
                {demande.DateDebutFormatee || suivi?.DateDebutFormatee} → {demande.DateFinFormatee || suivi?.DateFinFormatee}
              </p>
              <p className="text-xs text-emerald-500 mt-1">
                {demande.TotalDays || suivi?.TotalDays} jour(s) ouvré(s) • Créée le {demande.DateDemandeFormatee || suivi?.DateDemandeFormatee}
              </p>
            </div>

            {/* ============================================== */}
            {/* 🕐 TIMELINE DE VALIDATION */}
            {/* ============================================== */}
            <div className="relative pl-8">
              
              {/* ---------- ÉTAPE 1 : CRÉATION ---------- */}
              <div className="relative pb-6">
                {/* Point vert */}
                <div className="absolute left-[-1.65rem] top-1 w-8 h-8 rounded-full bg-emerald-100 border-2 border-emerald-500 flex items-center justify-center z-10">
                  <User size={14} className="text-emerald-600" />
                </div>
                {/* Carte */}
                <div className="bg-white border border-gray-200 rounded-xl p-3 ml-2">
                  <p className="text-sm font-semibold text-gray-800">Demande créée</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {demande.DateDemandeFormatee || suivi?.DateDemandeFormatee}
                  </p>
                  <p className="text-xs text-gray-400">Par vous-même</p>
                </div>
                {/* Ligne verticale */}
                <div className="absolute left-[-0.15rem] top-10 bottom-0 w-0.5 bg-gray-200"></div>
              </div>

              {/* ---------- ÉTAPES DE VALIDATION (N+1, N+2...) ---------- */}
              {etapes.map((etape, idx) => (
                <div key={idx} className="relative pb-6">
                  {/* Point coloré */}
                  <div className={`absolute left-[-1.65rem] top-1 w-8 h-8 rounded-full border-2 flex items-center justify-center z-10 ${getStatusBg(etape.Decision)}`}>
                    {getStatusIcon(etape.Decision)}
                  </div>
                  
                  {/* Carte étape */}
                  <div className={`rounded-xl p-3 ml-2 ${
                    etape.Decision === 'Approuve' ? 'bg-green-50 border border-green-200' :
                    etape.Decision === 'Rejete' ? 'bg-red-50 border border-red-200' :
                    'bg-amber-50 border border-amber-200'
                  }`}>
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-sm font-semibold text-gray-800">
                          N+{etape.Niveau} • {etape.ValidateurPrenom} {etape.ValidateurNom}
                        </p>
                        <p className="text-xs text-gray-500">
                          {etape.ValidateurRole === 'admin' ? '👑 Admin' : 
                           etape.ValidateurRole === 'manager' ? '💼 Manager' : '👤 Employé'}
                        </p>
                      </div>
                      <span className={`text-xs font-bold px-2 py-1 rounded-full ${
                        etape.Decision === 'Approuve' ? 'text-green-700 bg-green-200' :
                        etape.Decision === 'Rejete' ? 'text-red-700 bg-red-200' :
                        'text-amber-700 bg-amber-200'
                      }`}>
                        {etape.Decision === 'Approuve' ? '✅ Approuvé' :
                         etape.Decision === 'Rejete' ? '❌ Refusé' :
                         '⏳ En attente'}
                      </span>
                    </div>
                    
                    {etape.DateValidationFormatee && (
                      <p className="text-xs text-gray-500 mt-1">Le {etape.DateValidationFormatee}</p>
                    )}
                    
                    {etape.Commentaire && (
                      <p className="text-xs text-gray-600 mt-1 italic">"{etape.Commentaire}"</p>
                    )}
                  </div>
                  
                  {/* Ligne verticale (sauf dernière) */}
                  {idx < etapes.length - 1 && (
                    <div className="absolute left-[-0.15rem] top-10 bottom-0 w-0.5 bg-gray-200"></div>
                  )}
                </div>
              ))}

              {/* ---------- ÉTAPE FINALE : STATUT GLOBAL ---------- */}
              {etapes.length > 0 && (
                <div className="relative pb-2">
                  <div className="absolute left-[-1.65rem] top-1 w-8 h-8 rounded-full border-2 flex items-center justify-center z-10 bg-gray-100 border-gray-300">
                    {suivi?.StatusId === 2 ? (
                      <Check size={18} className="text-green-500" />
                    ) : suivi?.StatusId === 3 ? (
                      <Ban size={18} className="text-red-500" />
                    ) : (
                      <Clock size={18} className="text-amber-500" />
                    )}
                  </div>
                  <div className={`rounded-xl p-3 ml-2 ${
                    suivi?.StatusId === 2 ? 'bg-green-100 border border-green-300' :
                    suivi?.StatusId === 3 ? 'bg-red-100 border border-red-300' :
                    'bg-gray-100 border border-gray-200'
                  }`}>
                    <p className="text-sm font-bold text-gray-800">
                      {suivi?.StatusId === 3 ? '🎉 Congé validé !' :
                        suivi?.StatusId === 4 ? '❌ Demande refusée' :
                        suivi?.StatusId === 5 ? '🔄 Demande annulée' :
                        '⏳ En attente de validation finale'}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">{suivi?.StatusNom || demande?.StatusNom}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ================================================== */}
        {/* FOOTER STICKY - BOUTON FERMER */}
        {/* ================================================== */}
        <div className="sticky bottom-0 bg-white border-t p-4">
          <button
            onClick={onClose}
            className="w-full px-4 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition text-sm font-medium"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}